import { Injectable } from "@angular/core";
import { Select, Store } from "@ngxs/store";
import { Sandbox } from "../sandbox";
import {
  isCameraOpen,
  requestFaceImageQuality,
  setPadDisplay,
  faceTimeOut,
  setFaceImageLoad,
  getFaceMatchResult,
  setMatchLoading,
  setMatchLoaded,
  setCameraError
} from "../../store/actions/face.action";
import { FaceState } from "../../store/state/face.state";
import { combineLatest, Observable } from "rxjs";
import { GlobalState } from "../../store/state/global.state";
import { BioSealState } from "../../store/state/bioseal.state";
import { map, take } from "rxjs/operators";
import { FaceService } from "../../services/async/connectors/face.service";
import { PadSuccessResult } from "@id3/face-pad";
import { USECASE } from "../../model/usecase.model";
import { ErrorType } from "../../model/face.model";
import { ImageService } from "../../services/face-processor.service";
import { IdState } from "../../store/state/id.state";
@Injectable()
export class FaceSandbox extends Sandbox {
  @Select(FaceState.isCameraOpen) camera: Observable<boolean>;
  @Select(FaceState.isPadDisplayed) pad: Observable<boolean>;
  @Select(FaceState.isFaceImageLoading) faceImageLoading: Observable<boolean>;
  @Select(FaceState.getFaceState) face: Observable<string>;
  @Select(FaceState.getFace) faceImage: Observable<string>;
  @Select(FaceState.getMatchResult) matchResult: Observable<boolean>;
  @Select(FaceState.isMatchLoading) matchLoading: Observable<boolean>;
  @Select(FaceState.isMatchLoaded) matchLoaded: Observable<boolean>;
  @Select(FaceState.isLoaded) isLoaded: Observable<boolean>;
  @Select(FaceState.isStateless) isStateless: Observable<boolean>;
  @Select(FaceState.isError) isError: Observable<boolean>;
  @Select(FaceState.isUnSufficiantQualityError) isUnsufficiantQuality: Observable<ErrorType>;
  @Select(FaceState.isNoFaceDetectedError) isNoFaceDetected: Observable<ErrorType>;
  @Select(FaceState.isInvalidMediaTypeError) isInvalidMediaType: Observable<ErrorType>;
  @Select(FaceState.isCameraError) cameraError: Observable<boolean>;
  @Select(FaceState.isNoCameraFound) cameraNotFound: Observable<boolean>;
  @Select(FaceState.isPermissionDenied) permissionDenied: Observable<boolean>;
  @Select(FaceState.isCameraNotReadable) notReadable: Observable<boolean>;
  @Select(FaceState.isAbortError) cameraAbort: Observable<boolean>;
  @Select(BioSealState.getBiometricsType) biometrics: Observable<string>;
  @Select(IdState.getIdFile) idFile: Observable<File>;
  @Select(GlobalState.getToken) token: Observable<string>;
  @Select(GlobalState.getFaceUrl) faceUrl: Observable<string>;
  @Select(GlobalState.getBioSealUrl) biosealUrl: Observable<string>;
  @Select(GlobalState.isMobile) isMobile: Observable<boolean>;
  @Select(GlobalState.getLanguage) language: Observable<string>;
  @Select(GlobalState.getUseCase) useCase: Observable<USECASE>;


  constructor(private faceService: FaceService, private imageService: ImageService, private store: Store) {
    super(store);
  }

  isFacePad = false;
  isFaceMenu = true;

  public pvtCode: string;

  public proofPictures: Blob[];

  public maxSize = 1000000; //max 1Mo;

  get allowMultiple(): Observable<boolean> {
    return this.isLoaded;
  }

  get wsEndpoint(): Observable<string> {
    return this.faceUrl;
  }

  get pingBaseUrl(): Observable<string> {
    return this.faceUrl;
  }

  isCameraOpen() {
    return this.camera;
  }

  isPadDisplayed() {
    return this.pad;
  }

  clearFaceImageLoading() {
    this.store.dispatch(new setFaceImageLoad(false));
  }

  clearMatchLoading() {
    this.store.dispatch(new setMatchLoading(false));
    this.store.dispatch(new setMatchLoaded(false));
  }

  takePicture() {

    navigator.mediaDevices.getUserMedia({ video: true }).then(
      (stream) => {

        this.store.dispatch(new isCameraOpen(true));
        this.isFacePad = true;
        this.isFaceMenu = false;
        this.store.dispatch(new setFaceImageLoad(true));
        this.store.dispatch(new setCameraError({ isError: false, errorType: undefined }));
        this.isMobile.subscribe((item) => {
          if (item) {
            document.getElementById("facepad").requestFullscreen();
          }
        });
      },
      (err) => {
        this.onCameraError(err);
        this.isFaceMenu = false;
        this.isFacePad = true;
      }
    );
  }

  onCameraError(error: any) {
    const err = error.toString().split(':')[0];
    if (err === 'NotFoundError') {
      this.store.dispatch(new setCameraError({ isError: true, errorType: ErrorType.NO_CAMERA_FOUND }));
    }
    else if (err === 'NotAllowedError') {
      this.store.dispatch(new setCameraError({ isError: true, errorType: ErrorType.PERMISSION_DENIED }));
    }
    else if (err == 'NotReadableError') {
      this.store.dispatch(new setCameraError({ isError: true, errorType: ErrorType.CAMERA_NOT_READABLE }));
    }
    else {
      this.store.dispatch(new setCameraError({ isError: true, errorType: ErrorType.CAMERA_ABORT }));
    }
  }

  uploadPicture(e) {
    this.store.dispatch(new setFaceImageLoad(true));
    combineLatest([this.token, this.biosealUrl]).subscribe(([item1, item2]) => {
      if (e.target.files[0].size > this.maxSize) {
        this.imageService.resizeImage(e.target.files[0]).then(
          (image) => {
            this.store.dispatch(
              new requestFaceImageQuality({
                file: image,
                token: item1,
                url: item2,
              })
            );

          }
        );
      }
      else {
        this.store.dispatch(
          new requestFaceImageQuality({
            file: e.target.files[0],
            token: item1,
            url: item2,
          })
        );

      }

    });
  }

  isUpload(): Observable<boolean> {
    return this.biometrics.pipe(
      map((res) => {
        return res == "upload" || res == "both";
      })
    );
  }
  isFace(): Observable<boolean> {
    return this.biometrics.pipe(
      map((res) => {
        return res === "facial" || res === "both";
      })
    );
  }
  isBoth(): Observable<boolean> {
    return this.biometrics.pipe(
      map((res) => {
        return res === "both";
      })
    );
  }
  onFaceTimeOut() {
    this.store.dispatch(new faceTimeOut(true));
  }


  downloadPadPicture() {
    combineLatest([this.token, this.faceUrl, this.biosealUrl, this.idFile, this.useCase]).pipe(take(1)).subscribe(
      ([token, faceUrl, biosealUrl, idFile, useCase]) => {
        if (useCase === USECASE.ACCREDITATION) {
          this.store.dispatch(new setMatchLoading(true));
        }
        this.faceService
          .getLivenessProof(token, faceUrl, this.pvtCode)
          .subscribe((data) => {
            const bstr = atob(data.picture);
            let n = bstr.length;
            const u8arr = new Uint8Array(n);
            while (n--) {
              u8arr[n] = bstr.charCodeAt(n);
            }

            let file = new File([u8arr], "faceImage", { type: "image/jpeg" });
            if (useCase === USECASE.ACCREDITATION) {
              this.store.dispatch(
                new getFaceMatchResult({
                  pvtCode: this.pvtCode,
                  idFile: idFile,
                  faceImage: data.picture,
                  token: token,
                  url: faceUrl
                })
              );
            }
            else {
              this.store.dispatch(
                new requestFaceImageQuality({
                  file: file,
                  token: token,
                  url: biosealUrl
                })
              );
            }

            this.isFacePad = false;
            this.isFaceMenu = !(useCase === USECASE.ACCREDITATION)

            if (document.fullscreenElement) {
              document.exitFullscreen();
            }
          });
      }
    );
  }
  public getPvt(res: PadSuccessResult) {
    this.pvtCode = res.validationToken;
  }

  public getFaceImage(): Observable<any> {
    return this.faceImage.pipe(
      map((res) => {
        return 'data:image/png;base64,' + res;
      })
    )
  }

  public setPadDisplay(value) {
    this.store.dispatch(new setPadDisplay(value));
  }
  public getLanguage() {
    return this.language.pipe(
      map((res) => {
        return res ? res : navigator.language.substr(0, 2);
      })
    );
  }

}
